import { router } from "@inertiajs/react";
import { snakeCase } from "es-toolkit/compat";

type GtmConfig = {
  id: string;
  auth?: string;
  preview?: string;
};

type PageHead = Array<{
  tag: string;
  content?: string;
  attributes?: Record<string, string>;
}>;

export type InitialPage = {
  props: Record<string, unknown>;
  head?: PageHead;
};

function loadGtmScript(config: GtmConfig) {
  const { id, auth, preview } = config;
  const script = document.createElement("script");
  script.async = true;

  const params = new URLSearchParams({ id });
  if (auth) params.append("gtm_auth", auth);
  if (preview) params.append("gtm_preview", preview);
  params.append("gtm_cookies_win", "x");

  script.src = `https://www.googletagmanager.com/gtm.js?${params.toString()}`;
  document.head.appendChild(script);
}

function extractTitle(head?: PageHead): string {
  return head?.find((h) => h.tag === "title")?.content ?? document.title;
}

export function pushEvent(name: string, params: Record<string, unknown> = {}) {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: name,
    ...params,
  });
}

export function pushUniversalEvent(name: string, params: Record<string, unknown> = {}) {
  const normalized = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [snakeCase(key), value]),
  );
  pushEvent("universal_event", {
    universal_event_name: name,
    params: normalized,
  });
}

export function initGtm(initialPage: InitialPage) {
  const { props: initialProps, head: initialHead } = initialPage;
  const gtmConfig = initialProps.gtmConfig as GtmConfig | undefined;
  if (!gtmConfig?.id) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push([
    "consent",
    "default",
    {
      analytics_storage: "granted",
      ad_storage: "granted",
      ad_user_data: "granted",
      ad_personalization: "granted",
      functionality_storage: "granted",
      security_storage: "granted",
    },
  ]);

  loadGtmScript(gtmConfig);

  pushEvent("gtm.js", {
    "gtm.start": new Date().getTime(),
  });

  pushEvent("page_view", {
    page_title: extractTitle(initialHead),
    page_location: window.location.href,
    account_id: (initialProps.account as any)?.id ?? null,
    mens_version: initialProps.mensVersionOn,
  });

  router.on("navigate", (event: any) => {
    const page = event.detail.page;
    const pageProps = page.props as Record<string, unknown>;
    pushEvent("page_view", {
      page_title: extractTitle(page.head),
      page_location: window.location.href,
      account_id: (pageProps.account as any)?.id ?? null,
      mens_version: pageProps.mensVersionOn,
    });
  });
}
